import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { LoadingSpinner } from '../../../components/shared/LoadingSpinner';
import { CloseIcon, ExpandMoreIcon, EditIcon } from '../../../theme/icons';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';

const ScanScheduleModal = ({
  open,
  onClose,
  orgCode,
  accountCode,
  accountNumber,
  accountName,
  scanSchedules,
  isGettingScanSchedules,
  getScanSchedulesError,
  getScanSchedulesMessage,
  getScanSchedules,
  updateScanScheduleFrequency,
  isUpdatingScanSchedule,
  updateScanScheduleError,
  updateScanScheduleMessage,
  orgAccounts,
  getOrgAccounts,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [editingStatusRows, setEditingStatusRows] = useState({});
  const [editValues, setEditValues] = useState({});
  const [editStatusValues, setEditStatusValues] = useState({});
  const [isEditingAuditScanLimit, setIsEditingAuditScanLimit] = useState(false);
  const [auditScanLimitEditValue, setAuditScanLimitEditValue] = useState("");
  const [auditScanLimitError, setAuditScanLimitError] = useState("");

  const AUDIT_SCAN_LIMIT_MIN = 1;
  const AUDIT_SCAN_LIMIT_MAX = 10;

  // Prefer recurring schedule for schedule updates; fall back to first schedule
  const primarySchedule = scanSchedules?.length
    ? (scanSchedules.find((s) => s.scheduleType === "recurring") || scanSchedules[0])
    : null;

  // Source of truth for on-demand limit: accounts API for this account
  const selectedAccount = orgAccounts?.find((acc) => acc.accountCode === accountCode);
  const currentAuditScanLimit =
    selectedAccount?.auditScanLimit ?? selectedAccount?.audit_scan_limit;
  const displayAuditScanLimit =
    currentAuditScanLimit !== undefined && currentAuditScanLimit !== null
      ? currentAuditScanLimit
      : 1;

  const getAuditScanLimitValidationError = (value) => {
    if (value === "" || value === null || value === undefined) {
      return "On-demand scan limit is required.";
    }
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) {
      return "On-demand scan limit must be a number.";
    }
    if (parsed < AUDIT_SCAN_LIMIT_MIN) {
      return `On-demand scan limit cannot be less than ${AUDIT_SCAN_LIMIT_MIN}.`;
    }
    if (parsed > AUDIT_SCAN_LIMIT_MAX) {
      return `On-demand scan limit cannot be greater than ${AUDIT_SCAN_LIMIT_MAX}.`;
    }
    return "";
  };

  useEffect(() => {
    if (open && orgCode && accountCode) {
      getScanSchedules(orgCode, accountCode);
      if (getOrgAccounts) {
        getOrgAccounts(orgCode);
      }
    } else if (!open) {
      // Clear local state when modal closes
      setExpandedRows({});
      setEditingRows({});
      setEditingStatusRows({});
      setEditValues({});
      setEditStatusValues({});
      setIsEditingAuditScanLimit(false);
      setAuditScanLimitEditValue("");
      setAuditScanLimitError("");
    }
  }, [open, orgCode, accountCode, getScanSchedules, getOrgAccounts]);

  // Refresh schedules and accounts after successful update so limit stays current
  useEffect(() => {
    if (updateScanScheduleMessage && !isUpdatingScanSchedule && orgCode && accountCode) {
      getScanSchedules(orgCode, accountCode);
      if (getOrgAccounts) {
        getOrgAccounts(orgCode);
      }
      setIsEditingAuditScanLimit(false);
      setAuditScanLimitEditValue("");
      setAuditScanLimitError("");
    }
  }, [updateScanScheduleMessage, isUpdatingScanSchedule, orgCode, accountCode, getScanSchedules, getOrgAccounts]);

  const toggleRowExpansion = (scheduleId) => {
    setExpandedRows(prev => ({
      ...prev,
      [scheduleId]: !prev[scheduleId]
    }));
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "paused":
        return "warning";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  const getScheduleTypeColor = (scheduleType) => {
    switch (scheduleType?.toLowerCase()) {
      case "recurring":
        return "primary";
      case "on_demand":
        return "secondary";
      default:
        return "default";
    }
  };

  const handleEditClick = (scheduleId, currentFrequency) => {
    setEditingRows(prev => ({ ...prev, [scheduleId]: true }));
    setEditValues(prev => ({ ...prev, [scheduleId]: currentFrequency || "" }));
  };

  const handleEditStatusClick = (scheduleId, currentStatus) => {
    setEditingStatusRows(prev => ({ ...prev, [scheduleId]: true }));
    // Map "paused" to "pause" for UI display
    const displayStatus = currentStatus === "paused" ? "pause" : currentStatus;
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: displayStatus || "" }));
  };

  const handleCancelEdit = (scheduleId) => {
    setEditingRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditValues(prev => ({ ...prev, [scheduleId]: undefined }));
  };

  const handleCancelStatusEdit = (scheduleId) => {
    setEditingStatusRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: undefined }));
  };

  const handleSaveEdit = (scheduleId, originalFrequency, originalStatus, existingAuditScanLimit) => {
    const newFrequency = editValues[scheduleId];
    const newStatusValue = editStatusValues[scheduleId];
    // Map "pause" back to "paused" for API
    const newStatus = newStatusValue === "pause" ? "paused" : newStatusValue;
    
    // Always send both frequency and status - use new value if changed, otherwise use original
    const frequencyToSend = newFrequency && newFrequency !== originalFrequency 
      ? newFrequency 
      : originalFrequency;
    const statusToSend = newStatus && newStatus !== originalStatus 
      ? newStatus 
      : originalStatus;
    
    // Only update if something actually changed
    const frequencyChanged = newFrequency && newFrequency !== originalFrequency;
    const statusChanged = newStatus && newStatus !== originalStatus;
    
    if (frequencyChanged || statusChanged) {
      updateScanScheduleFrequency(
        orgCode, 
        accountCode, 
        scheduleId, 
        frequencyToSend,
        statusToSend,
        existingAuditScanLimit
      );
    }
    setEditingRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditingStatusRows(prev => ({ ...prev, [scheduleId]: false }));
    setEditValues(prev => ({ ...prev, [scheduleId]: undefined }));
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: undefined }));
  };

  const handleFrequencyChange = (scheduleId, value) => {
    setEditValues(prev => ({ ...prev, [scheduleId]: value }));
  };

  const handleStatusChange = (scheduleId, value) => {
    setEditStatusValues(prev => ({ ...prev, [scheduleId]: value }));
  };

  const handleEditAuditScanLimitClick = () => {
    setIsEditingAuditScanLimit(true);
    setAuditScanLimitEditValue(displayAuditScanLimit);
    setAuditScanLimitError("");
  };

  const handleCancelAuditScanLimitEdit = () => {
    setIsEditingAuditScanLimit(false);
    setAuditScanLimitEditValue("");
    setAuditScanLimitError("");
  };

  const handleAuditScanLimitInputChange = (value) => {
    // Numbers only (allow empty while typing)
    if (value === "" || /^\d+$/.test(String(value))) {
      const nextValue = value === "" ? "" : parseInt(value, 10);
      setAuditScanLimitEditValue(nextValue);
      setAuditScanLimitError(getAuditScanLimitValidationError(nextValue));
    }
  };

  const handleIncrementAuditScanLimit = () => {
    const numValue = parseInt(auditScanLimitEditValue, 10);
    const current = Number.isNaN(numValue) ? AUDIT_SCAN_LIMIT_MIN : numValue;
    if (current >= AUDIT_SCAN_LIMIT_MAX) {
      setAuditScanLimitEditValue(AUDIT_SCAN_LIMIT_MAX);
      setAuditScanLimitError(`On-demand scan limit cannot be greater than ${AUDIT_SCAN_LIMIT_MAX}.`);
      return;
    }
    const nextValue = current + 1;
    setAuditScanLimitEditValue(nextValue);
    setAuditScanLimitError(getAuditScanLimitValidationError(nextValue));
  };

  const handleDecrementAuditScanLimit = () => {
    const numValue = parseInt(auditScanLimitEditValue, 10);
    const current = Number.isNaN(numValue) ? AUDIT_SCAN_LIMIT_MIN : numValue;
    if (current <= AUDIT_SCAN_LIMIT_MIN) {
      setAuditScanLimitEditValue(AUDIT_SCAN_LIMIT_MIN);
      setAuditScanLimitError(`On-demand scan limit cannot be less than ${AUDIT_SCAN_LIMIT_MIN}.`);
      return;
    }
    const nextValue = current - 1;
    setAuditScanLimitEditValue(nextValue);
    setAuditScanLimitError(getAuditScanLimitValidationError(nextValue));
  };

  const handleSaveAuditScanLimit = () => {
    if (!primarySchedule) return;

    const validationError = getAuditScanLimitValidationError(auditScanLimitEditValue);
    if (validationError) {
      setAuditScanLimitError(validationError);
      return;
    }

    const parsedLimit = parseInt(auditScanLimitEditValue, 10);
    if (parsedLimit === displayAuditScanLimit && currentAuditScanLimit != null) {
      setIsEditingAuditScanLimit(false);
      setAuditScanLimitEditValue("");
      setAuditScanLimitError("");
      return;
    }

    setAuditScanLimitError("");
    updateScanScheduleFrequency(
      orgCode,
      accountCode,
      primarySchedule.scheduleId,
      primarySchedule.frequency,
      primarySchedule.status,
      parsedLimit
    );
  };

  const parsedAuditScanLimitEdit = parseInt(auditScanLimitEditValue, 10);
  const isAuditScanLimitValid = !getAuditScanLimitValidationError(auditScanLimitEditValue);
  const auditScanLimitHasChanged =
    isAuditScanLimitValid &&
    (currentAuditScanLimit == null || parsedAuditScanLimitEdit !== currentAuditScanLimit);

  const frequencyOptions = ["hourly", "daily", "weekly", "monthly"];
  const statusOptions = ["active", "pause"]; // UI shows "pause", API receives "paused"

  // Expandable row component for detailed information
  const ExpandableRow = ({ schedule }) => {
    const isExpanded = expandedRows[schedule.scheduleId];
    const isEditing = editingRows[schedule.scheduleId];
    const isEditingStatus = editingStatusRows[schedule.scheduleId];
    const currentEditValue = editValues[schedule.scheduleId];
    const currentStatusValue = editStatusValues[schedule.scheduleId];
    const originalFrequency = schedule.frequency;
    const originalStatus = schedule.status;
    // Map "paused" to "pause" for comparison
    const originalStatusDisplay = originalStatus === "paused" ? "pause" : originalStatus;
    const frequencyChanged = currentEditValue && currentEditValue !== originalFrequency;
    const statusChanged = currentStatusValue && currentStatusValue !== originalStatusDisplay;
    const hasChanged = frequencyChanged || statusChanged;
    const canEditFrequency = schedule.scheduleType === "recurring";
    const canEditStatus = true; // Status can be edited for all schedule types
    
    return (
      <>
        <TableRow>
          <TableCell>
            <IconButton
              size="small"
              onClick={() => toggleRowExpansion(schedule.scheduleId)}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {schedule.scheduleId}
            </Typography>
          </TableCell>
          <TableCell>
            <Chip
              label={schedule.scheduleType || "N/A"}
              size="small"
              color={getScheduleTypeColor(schedule.scheduleType)}
            />
          </TableCell>
          <TableCell>
            {isEditing && canEditFrequency ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={currentEditValue || ""}
                    onChange={(e) => handleFrequencyChange(schedule.scheduleId, e.target.value)}
                    disabled={isUpdatingScanSchedule}
                  >
                    {frequencyOptions.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {freq.charAt(0).toUpperCase() + freq.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(isEditing || isEditingStatus) && (
                  <>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={!hasChanged || isUpdatingScanSchedule}
                      onClick={() => handleSaveEdit(schedule.scheduleId, originalFrequency, originalStatus, currentAuditScanLimit)}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: hasChanged ? '#4caf50' : '#e0e0e0',
                        color: hasChanged ? 'white' : '#9e9e9e',
                        '&:hover': {
                          backgroundColor: hasChanged ? '#45a049' : '#d0d0d0',
                        }
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isUpdatingScanSchedule}
                      onClick={() => {
                        handleCancelEdit(schedule.scheduleId);
                        if (isEditingStatus) handleCancelStatusEdit(schedule.scheduleId);
                      }}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#da190b',
                        }
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {schedule.frequency ? schedule.frequency.charAt(0).toUpperCase() + schedule.frequency.slice(1) : "N/A"}
                </Typography>
                {canEditFrequency && (
                  <IconButton
                    size="small"
                    onClick={() => handleEditClick(schedule.scheduleId, schedule.frequency)}
                    disabled={isUpdatingScanSchedule}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </TableCell>
          <TableCell>
            {isEditingStatus ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={currentStatusValue || ""}
                    onChange={(e) => handleStatusChange(schedule.scheduleId, e.target.value)}
                    disabled={isUpdatingScanSchedule}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!(isEditing && canEditFrequency) && (
                  <>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={!hasChanged || isUpdatingScanSchedule}
                      onClick={() => handleSaveEdit(schedule.scheduleId, originalFrequency, originalStatus, currentAuditScanLimit)}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: hasChanged ? '#4caf50' : '#e0e0e0',
                        color: hasChanged ? 'white' : '#9e9e9e',
                        '&:hover': {
                          backgroundColor: hasChanged ? '#45a049' : '#d0d0d0',
                        }
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isUpdatingScanSchedule}
                      onClick={() => {
                        handleCancelStatusEdit(schedule.scheduleId);
                        if (isEditing && canEditFrequency) handleCancelEdit(schedule.scheduleId);
                      }}
                      sx={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#da190b',
                        }
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={schedule.status === "paused" ? "Pause" : (schedule.status || "N/A")}
                  size="small"
                  color={getStatusColor(schedule.status)}
                />
                {canEditStatus && (
                  <IconButton
                    size="small"
                    onClick={() => handleEditStatusClick(schedule.scheduleId, schedule.status)}
                    disabled={isUpdatingScanSchedule}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2">
              {formatDate(schedule.nextRunDate)}
            </Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="h6" gutterBottom component="div">
                  Schedule Details
                </Typography>
                <Table size="small" aria-label="schedule details">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: '30%' }}><strong>Resource Scope Type</strong></TableCell>
                      <TableCell>{schedule.resourceScopeType || "N/A"}</TableCell>
                      <TableCell sx={{ width: '30%' }}><strong>Account Number</strong></TableCell>
                      <TableCell>{schedule.accountNumber || "N/A"}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Last Run Date</strong></TableCell>
                      <TableCell>{formatDate(schedule.lastRunDate)}</TableCell>
                      <TableCell><strong>Created At</strong></TableCell>
                      <TableCell>{formatDate(schedule.createdAt)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Created By</strong></TableCell>
                      <TableCell colSpan={3}>{schedule.createdBy || "N/A"}</TableCell>
                    </TableRow>
                    {schedule.tag && schedule.tag.length > 0 && (
                      <TableRow>
                        <TableCell><strong>Tags</strong></TableCell>
                        <TableCell colSpan={3}>
                          {schedule.tag.join(", ")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        style: {
          minHeight: "80vh",
          maxHeight: "95vh",
          width: "95vw",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          Scan Schedules for Account: {accountNumber} {accountName && `- ${accountName}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading Spinner */}
        {isGettingScanSchedules && <LoadingSpinner />}

        {/* Error Message */}
        {getScanSchedulesError && !isGettingScanSchedules && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading scan schedules: {getScanSchedulesError.message || JSON.stringify(getScanSchedulesError)}
          </Alert>
        )}

        {/* Update Success Message */}
        {updateScanScheduleMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {updateScanScheduleMessage}
          </Alert>
        )}

        {/* Update Error Message */}
        {updateScanScheduleError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error updating schedule: {updateScanScheduleError.message || JSON.stringify(updateScanScheduleError)}
          </Alert>
        )}

        {/* Update Loading Spinner */}
        {isUpdatingScanSchedule && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <LoadingSpinner />
          </Box>
        )}

        {/* On-Demand Scan Limit Card */}
        {scanSchedules && !isGettingScanSchedules && primarySchedule && (
          <Paper
            elevation={1}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  On-Demand Scan Limit
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Maximum number of on-demand scans this account can request from CloudCatcher.
                </Typography>
              </Box>

              {isEditingAuditScanLimit ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'flex-end' }, flexShrink: 0, gap: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        width: { xs: 64, sm: 72 },
                        height: { xs: 40, sm: 44 },
                        border: '1px solid',
                        borderColor: auditScanLimitError ? 'error.main' : 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                      }}
                    >
                      <TextField
                        type="number"
                        value={auditScanLimitEditValue}
                        onChange={(e) => handleAuditScanLimitInputChange(e.target.value)}
                        onFocus={(e) => e.target.select()}
                        disabled={isUpdatingScanSchedule}
                        error={Boolean(auditScanLimitError)}
                        inputProps={{
                          min: AUDIT_SCAN_LIMIT_MIN,
                          max: AUDIT_SCAN_LIMIT_MAX,
                          step: 1,
                          'aria-label': 'On-demand scan limit',
                          style: {
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 600,
                            lineHeight: 1,
                            padding: 0,
                            margin: 0,
                          },
                        }}
                        variant="standard"
                        sx={{
                          flex: 1,
                          height: '100%',
                          '& .MuiInput-root': {
                            height: '100%',
                          },
                          '& .MuiInputBase-root': {
                            height: '100%',
                          },
                          '& .MuiInput-underline:before': { borderBottom: 'none' },
                          '& .MuiInput-underline:after': { borderBottom: 'none' },
                          '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                          '& .MuiInputBase-input': {
                            textAlign: 'center !important',
                            height: '100% !important',
                            boxSizing: 'border-box',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0,
                            },
                            '&[type=number]': {
                              MozAppearance: 'textfield',
                            },
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          borderLeft: '1px solid',
                          borderColor: auditScanLimitError ? 'error.main' : 'divider',
                          width: 22,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isUpdatingScanSchedule ? 'default' : 'pointer',
                            borderBottom: '1px solid',
                            borderColor: auditScanLimitError ? 'error.main' : 'divider',
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: isUpdatingScanSchedule ? 'transparent' : 'action.hover',
                              color: 'text.primary',
                            },
                          }}
                          onClick={() => !isUpdatingScanSchedule && handleIncrementAuditScanLimit()}
                        >
                          <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, fontWeight: 600 }}>
                            ▲
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isUpdatingScanSchedule ? 'default' : 'pointer',
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: isUpdatingScanSchedule ? 'transparent' : 'action.hover',
                              color: 'text.primary',
                            },
                          }}
                          onClick={() => !isUpdatingScanSchedule && handleDecrementAuditScanLimit()}
                        >
                          <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, fontWeight: 600 }}>
                            ▼
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      color="success"
                      disabled={isUpdatingScanSchedule}
                      onClick={handleSaveAuditScanLimit}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        backgroundColor: auditScanLimitHasChanged ? '#4caf50' : '#e0e0e0',
                        color: auditScanLimitHasChanged ? 'white' : '#9e9e9e',
                        '&:hover': {
                          backgroundColor: auditScanLimitHasChanged ? '#45a049' : '#d0d0d0',
                        },
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isUpdatingScanSchedule}
                      onClick={handleCancelAuditScanLimitEdit}
                      sx={{
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        backgroundColor: '#f44336',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#da190b',
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {auditScanLimitError && (
                    <Typography variant="caption" color="error" sx={{ maxWidth: 280, textAlign: { xs: 'left', sm: 'right' } }}>
                      {auditScanLimitError}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      lineHeight: 1.2,
                      minWidth: 40,
                      textAlign: 'right',
                      color: 'text.primary',
                    }}
                  >
                    {displayAuditScanLimit}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleEditAuditScanLimitClick}
                    disabled={isUpdatingScanSchedule}
                    aria-label="Edit on-demand scan limit"
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* Schedules Table */}
        {scanSchedules && !isGettingScanSchedules && (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader aria-label="scan schedules table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>Expand</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Schedule ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Schedule Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Frequency</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Next Run Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scanSchedules.length > 0 ? (
                  // Sort: recurring schedules first, then on_demand
                  [...scanSchedules]
                    .sort((a, b) => {
                      if (a.scheduleType === 'recurring' && b.scheduleType !== 'recurring') return -1;
                      if (a.scheduleType !== 'recurring' && b.scheduleType === 'recurring') return 1;
                      return 0;
                    })
                    .map((schedule) => (
                      <ExpandableRow key={schedule.scheduleId} schedule={schedule} />
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No scan schedules found for this account.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* No Data Message */}
        {!scanSchedules && !isGettingScanSchedules && !getScanSchedulesError && (
          <Typography variant="body1" color="textSecondary" align="center">
            No scan schedule data available for this account.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScanScheduleModal;
